import React, { useState, useContext, useRef, useEffect } from 'react';

import {
  ApCheckbox,
  ApButtonSecondary,
  ApMenuContext,
  ApIcon,
  colors,
} from '@alixpartners/ui-components';
import {
  Wrapper,
  CheckboxWrapper,
  StyledSearch,
  FilterListWrapper,
  ActionsWrapper,
  SearchWrapper,
  Label,
  StyledMenu,
  IconWrapper,
  LabelWrapper,
  NoDataWrapper,
} from './FilterMenuContent.styles';
import { IOption, IQueryFilter } from 'utils/types';
import { IFilterProp } from 'components/FilterPanel/utils';
import { L1Suffix, L2Suffix } from 'utils/constants';

interface IProps {
  filterId: string;
  options: IOption[];
  onChange(filter: IQueryFilter): void;
  onL2Change(
    filterId: string,
    selectedFilter: IFilterProp,
    isSelected?: boolean
  ): void;
  onClear(filterIds: string[]): void;
  parentId?: string;
}

const getPartialState = (options: IOption[]) => {
  const selectedItems = options.filter((item) => item.isSelected);

  return selectedItems.length !== options.length && selectedItems.length > 0;
};

export const FilterMenuContent: React.FC<IProps> = ({
  filterId,
  options,
  onChange,
  onL2Change,
  onClear,
  parentId,
}) => {
  const context = useContext(ApMenuContext);
  const [search, setSearch] = useState('');
  const [hasScroll, setHasScroll] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const filteredOptions = search
    ? options.filter(({ label }) =>
        label.toLowerCase().includes(search.toLowerCase())
      )
    : options;
  const hasSelectedOptions = !!options.find((item) => item.isSelected);
  const onSearchChange = (query: any) => {
    setSearch(query);
  };
  const onSearchClear = () => {
    setSearch('');
  };
  const handleClear = () => {
    const hasL2Options = options.some((item) => item.options);

    onClear([
      filterId,
      ...(hasL2Options
        ? [`${filterId}${L1Suffix}`, `${filterId}${L2Suffix}`]
        : []),
    ]);

    context.closeMenu();
  };
  const setScrollState = () => {
    const srollContainer = scrollContainerRef && scrollContainerRef.current;

    if (srollContainer) {
      const hasScrollbar =
        srollContainer.scrollHeight > srollContainer.clientHeight;

      // Hide search input if no scrollbar
      setHasScroll(hasScrollbar);
    }
  };

  useEffect(() => {
    setScrollState();
  }, [scrollContainerRef]);

  useEffect(() => {
    window.addEventListener('resize', setScrollState);

    return () => {
      window.removeEventListener('resize', setScrollState);
    };
  }, []);

  return (
    <Wrapper>
      {(search || hasScroll) && (
        <SearchWrapper>
          <StyledSearch
            id={`${filterId}-search`}
            onChange={onSearchChange}
            onClear={onSearchClear}
            value={search}
          />
        </SearchWrapper>
      )}

      <FilterListWrapper ref={scrollContainerRef}>
        {filteredOptions.map(({ label, isSelected, id, options }) => {
          const shouldUseL2Handler = !!options || parentId;
          const changeHandler = () =>
            !shouldUseL2Handler
              ? onChange({ [filterId]: label })
              : onL2Change(filterId, {
                  l1Code: parentId || id.toString(),
                  l2Code: parentId && id.toString(),
                });
          const isPartialSelected = options && getPartialState(options);

          return (
            <CheckboxWrapper key={`${filterId}-${label}`}>
              <div onClick={changeHandler}>
                {!isPartialSelected ? (
                  <ApCheckbox
                    id={`${filterId}-${label}`}
                    checked={isSelected}
                    onChange={changeHandler}
                  />
                ) : (
                  <IconWrapper>
                    <ApIcon
                      iconName="custom_dash"
                      iconSize={20}
                      iconColor="white"
                    />
                  </IconWrapper>
                )}
              </div>

              {options ? (
                <StyledMenu
                  toggleVariant="click"
                  button={
                    <LabelWrapper>
                      <Label title={label}>{label}</Label>
                      <ApIcon
                        iconName="baseline_keyboard_arrow_right"
                        iconSize={26}
                        iconColor={colors.grey6}
                      />
                    </LabelWrapper>
                  }
                >
                  <FilterMenuContent
                    options={options}
                    filterId={filterId}
                    onChange={onChange}
                    onL2Change={onL2Change}
                    onClear={() =>
                      onL2Change(filterId, { l1Code: id.toString() }, false)
                    }
                    parentId={id.toString()}
                  />
                </StyledMenu>
              ) : (
                <Label title={label} onClick={changeHandler}>
                  {label}
                </Label>
              )}
            </CheckboxWrapper>
          );
        })}
        {search && filteredOptions.length === 0 && (
          <NoDataWrapper>No results found</NoDataWrapper>
        )}
      </FilterListWrapper>
      <ActionsWrapper>
        <ApButtonSecondary disabled={!hasSelectedOptions} onClick={handleClear}>
          Clear
        </ApButtonSecondary>
      </ActionsWrapper>
    </Wrapper>
  );
};
